import type { Volume } from './types';

/** A reduced-resolution copy of the HU volume for the 3D GPU texture. */
export interface Volume3D {
    data: Int16Array; // z-major, index = z*ny*nx + y*nx + x (same layout as Volume)
    nz: number;
    ny: number;
    nx: number;
    factor: number; // downsample factor vs the full volume (1 = full res)
}

// The full volume (~460 MB at 607×616×616 int16) is too large for a 3D texture
// on typical GPUs. Cap the longest edge instead of using a fixed factor so both
// small and large scans land in a safe texture budget (~350³ ≈ 85 MB).
const MAX_EDGE = 350;

// One downsampled copy per loaded volume — toggling the 3D view must not
// recompute the reduction (it walks all ~230M voxels). The z-flipped variant
// (see `flipVertical` below) is derived from the plain copy on first request
// and cached alongside it, so toggling orientation never re-walks the volume
// or re-runs the mean-pool either.
const cache = new WeakMap<Int16Array, { plain: Volume3D; flipped?: Volume3D }>();

/** Mean-pool the volume by an integer factor chosen from MAX_EDGE.
 *  Mean (not stride-pick) keeps CBCT noise from shimmering in the 3D render. */
function downsample(vol: Volume): Volume3D {
    const { data, nz, ny, nx } = vol;
    const factor = Math.max(1, Math.ceil(Math.max(nz, ny, nx) / MAX_EDGE));
    if (factor === 1) {
        return { data, nz, ny, nx, factor };
    }

    const dnz = Math.floor(nz / factor);
    const dny = Math.floor(ny / factor);
    const dnx = Math.floor(nx / factor);
    const outData = new Int16Array(dnz * dny * dnx);
    const block = factor * factor * factor;

    for (let z = 0; z < dnz; z++) {
        for (let y = 0; y < dny; y++) {
            const dstRow = (z * dny + y) * dnx;
            for (let x = 0; x < dnx; x++) {
                let sum = 0;
                for (let dz = 0; dz < factor; dz++) {
                    const srcSlice = (z * factor + dz) * ny * nx;
                    for (let dy = 0; dy < factor; dy++) {
                        const srcRow = srcSlice + (y * factor + dy) * nx + x * factor;
                        for (let dx = 0; dx < factor; dx++) sum += data[srcRow + dx];
                    }
                }
                outData[dstRow + x] = (sum / block) | 0;
            }
        }
    }

    return { data: outData, nz: dnz, ny: dny, nx: dnx, factor };
}

/** Reverses the z order of a downsampled volume — same "which end is
 *  superior" correction as `displayZ` in volume.ts, applied to the actual
 *  voxel data (rather than a per-draw index remap) since the 3D mapper reads
 *  the whole texture at once. */
function reverseZ(v: Volume3D): Volume3D {
    const { data, nz, ny, nx, factor } = v;
    const sliceLen = ny * nx;
    const out = new Int16Array(data.length);
    for (let z = 0; z < nz; z++) {
        const src = z * sliceLen;
        out.set(data.subarray(src, src + sliceLen), (nz - 1 - z) * sliceLen);
    }
    return { data: out, nz, ny, nx, factor };
}

/**
 * Downsampled copy of the HU volume for the 3D GPU texture, oriented per
 * `flipVertical` (see `displayZ` in volume.ts — same superior/inferior
 * correction as the 2D reslice views, applied here since the whole texture
 * uploads at once rather than one slice at a time).
 */
export function getVolume3D(vol: Volume, flipVertical = false): Volume3D {
    let entry = cache.get(vol.data);
    if (!entry) {
        entry = { plain: downsample(vol) };
        cache.set(vol.data, entry);
    }
    if (!flipVertical) return entry.plain;
    if (!entry.flipped) entry.flipped = reverseZ(entry.plain);
    return entry.flipped;
}
